const _ = require('lodash');
const Types = require('../action-types');
const { safeThrow } = require('../utils/safe-throw');
const { push: Push, replace: Replace } = require('connected-react-router');
const AuthRoutes = require('routes/auth/config');
const SsoUtils = require('../utils/sso');
const AuthConstants = require('services/auth-management/constants');
const RoutingTable = require('services/auth-management/routing-table');
const AuthDebug = require('services/auth-management/debug');
const MfaCooldown = require('services/auth-management/mfa-cooldown');
const { isInvalidApiTokenError } = require('utils/auth-error');

module.exports = (context) => {

    const selectors = context.selectors.all;
    const actions   = context.actions;
    const redux = context.redux.hooks;
    const internals = {
        AUTH_RATE_LIMIT_FAILURES: new Set([
            Types.AUTH.LOGIN.FAILURE,
            Types.AUTH.LOGIN_TOKEN.FAILURE,
            Types.AUTH.LOGIN_SSO_TOKEN.FAILURE,
            Types.AUTH.START_MFA_SETUP.FAILURE,
            Types.AUTH.RESEND_MFA_CHALLENGE.FAILURE,
            Types.AUTH.VERIFY_MFA.FAILURE,
            Types.SIGNUP.AUTHENTICATE.CHECK_PASSWORD.FAILURE,
            Types.SIGNUP.AUTHENTICATE.COMPLETE.FAILURE,
            Types.SIGNUP.AUTHENTICATE.COMPLETE_SSO.FAILURE,
            Types.SIGNUP.COMPLETE.FAILURE
        ]),
        isAuthFailure(action) {

            const state = redux.getState();

            if (!selectors.getIsAuthenticated?.(state)) {
                return false;
            }

            return isInvalidApiTokenError(action?.payload?.error, {
                activeToken: selectors.getApiToken?.(state)
            });
        },
        resolveSSO({ schoolId, sso }) {

            if (sso) {
                return SsoUtils.normalizeSSO(sso);
            }

            const school = selectors.getSchoolById(redux.getState(), schoolId);
            return SsoUtils.normalizeSSO(school?.sso);
        },
        redirectToSSOLogin({ email, schoolId, sso }) {

            const resolvedSSO = internals.resolveSSO({ schoolId, sso });

            const contextPayload = {
                email,
                schoolId,
                sso: resolvedSSO,
                authMethod: AuthConstants.authFlow.methods.sso,
                fetchedProgress: false
            };

            redux.dispatch(actions.signup.updateContext(contextPayload));
            redux.dispatch(actions.auth.updateLoginContext({
                authMethod: AuthConstants.authFlow.methods.sso,
                email,
                schoolId,
                sso: resolvedSSO
            }));
            redux.dispatch(Push(AuthConstants.authFlow.routes.login.sso));
        },
        recordReduxActionError(action) {

            const error = action?.payload?.error;

            if (!error) {
                return;
            }

            AuthDebug.recordRecentError?.({
                source: 'redux-action',
                message: `${action.type} failed`,
                error,
                details: {
                    actionType: action.type,
                    request: action.payload?.request
                }
            });
        },
        shouldForceLogoutOnUnauthorized(action) {

            return internals.isAuthFailure(action);
        },
        getLoginFailureMessage({ errorData }) {

            if (errorData.message?.includes('valid email')) {
                return {
                    key: 'login',
                    title: 'Login Error',
                    message: 'Invalid email, please check email again.'
                };
            }

            if (errorData.message === 'Email has not yet been verified') {
                return {
                    key: 'login',
                    title: 'Login Error',
                    message: 'We sent you an email! Please verify your account.'
                };
            }

            if (errorData.message === 'User or Password is invalid') {
                return {
                    key: 'login',
                    title: 'Login Error',
                    message: 'Invalid email or password. Please ensure you\'re using the correct personal or school email.'
                };
            }

            if (errorData.statusCode === 401) {
                return {
                    key: 'login',
                    title: 'Login Error',
                    message: 'Sorry, the account has been disabled. Please contact Support to re-enable your account.'
                };
            }

            return {
                key: 'login',
                title: 'Login Error',
                message: 'Invalid email or password. Please ensure you\'re using the correct personal or school email.'
            };
        },
        getProfilePhoneCooldownMessage({ error, request }) {

            const response = error?.response || {};
            const errorData = response.data || {};
            const isPhoneUpdate = ['phone', 'phoneCountry'].some((field) => Object.prototype.hasOwnProperty.call(request || {}, field));
            const isMfaError = typeof errorData.code === 'string' && errorData.code.startsWith('MFA_');

            if (response.status !== 429 || (!isPhoneUpdate && !isMfaError)) {
                return MESSAGES.DEFAULT;
            }

            const retryAfterSeconds = Number(errorData.retryAfterSeconds);

            if (!Number.isFinite(retryAfterSeconds) || retryAfterSeconds <= 0) {
                return 'Your phone number cannot be changed right now. Please try again later.';
            }

            return `Your phone number cannot be changed right now. Try again in ${Math.ceil(retryAfterSeconds)} seconds.`;
        },
        routeRateLimitedChallenge(action) {

            const errorData = action.payload?.error?.response?.data || {};

            if (!errorData.mfaToken) {
                return;
            }

            const state = redux.getState();
            const signupContext = selectors.getSignupContext(state) || {};
            const loginContext = selectors.getLoginContext?.(state) || {};
            const signupFailureTypes = new Set([
                Types.AUTH.LOGIN_TOKEN.FAILURE,
                Types.SIGNUP.AUTHENTICATE.COMPLETE.FAILURE,
                Types.SIGNUP.AUTHENTICATE.COMPLETE_SSO.FAILURE,
                Types.SIGNUP.COMPLETE.FAILURE
            ]);
            const signupOwnsChallenge = signupFailureTypes.has(action.type)
                && !!signupContext.verificationToken;
            const currentContext = signupOwnsChallenge ? signupContext : loginContext;

            redux.dispatch(actions.app.clearAppMessage({
                key: 'login'
            }));

            RoutingTable.routePendingMfa({
                currentContext,
                dispatch: redux.dispatch,
                request: action.payload?.request,
                response: {
                    ...errorData,
                    status: errorData.mfaNeedsPhone
                        ? 'mfa_setup_required'
                        : 'mfa_required'
                },
                source: currentContext.authFlowSource,
                updateContext: signupOwnsChallenge
                    ? actions.signup.updateContext
                    : actions.auth.updateLoginContext
            });
        }
    };

    const MESSAGES = {
        BAD_LOGIN:                  'Oops! Invalid credentials.',
        RESET_PASSWORD:             'We sent you a password reset email!',
        BAD_FIELDS:                 'Oops! Some fields are invalid.',
        BAD_CREATE_ACCOUNT:         'Oops! Invalid email.',
        EMAIL_ALREADY_EXISTS:       'An account already exists for this email.',
        ACCOUNT_CLOSED:             'That account has been disabled. Please contact "helpdesk@nearpeer.net" for additional help.',
        UNVERIFIED_EMAIL:           'We sent you an email! Please verify your account.',
        INVALID_PHONE:              'Oops! That phone number looks invalid.',
        INVALID_EMAIL:              'Invalid email',
        BAD_UPDATE_PROFILE:         'Oops! Something went wrong updating your profile– there may be an invalid field.',
        BAD_COMPLETE_PROFILE:       'Oops! Something went wrong completing your profile– there may be an invalid field.',
        LSU_2021_STUDENTS:          'Access is currently limited to incoming 2021 students',
        DEFAULT:                    'Oops! Something went wrong.', // Note: this default msg will be ignored
        IMAGE_TOO_LARGE:            'Picture too large, please choose another one.',
        IMAGE_FAIL_DEFAULT:         'There was a problem updating your profile picture.',
        ALREADY_HAVE_ACCOUNT:       'Please login, you already have an account.',
        LINK_EXPIRED_EMAIL_SENT:    'This link has expired, we sent you a new email.',
        LINK_EXPIRED_SSO:           'This link has expired, please login with SSO.',
        INVITE_LINK_EXPIRED:        'This invitation link has expired.',
        CLICK_EMAIL_LINK_AGAIN:     'Please click your email link again!'
    };

    const MESSAGE_SELECTION_RULES = [
        [Types.SIGNUP.IDENTIFY.COMPLETE.FAILURE, ({ payload: { error, request: { email, schoolId } } }) => {

            const state = redux.getState();

            const school = selectors.getSchoolById(state, schoolId) || {};
            const errorData = error?.response?.data || {};
            const status = error?.response?.status;

            let popupTitle = 'Unrecognized Email Address';
            let popupMsg = school.customSignupErrMsg || 'Please use your school email or the email you applied with. Click "Trouble Logging In?" if you need help.';

            if (status === 400) {
                if (errorData.message === 'This email address is already in use. Please use a different one.') {

                    const {
                        authMethod
                    } = selectors.getSignupContext(state);

                    // Existing SSO accounts should continue in the login flow.
                    if (authMethod === AuthConstants.authFlow.methods.sso) {
                        internals.redirectToSSOLogin({
                            email,
                            schoolId,
                            sso: selectors.getSignupContext(state)?.sso
                        });

                        return null;
                    }

                    popupTitle = 'Email Already Registered';
                    popupMsg = MESSAGES.CLICK_EMAIL_LINK_AGAIN;
                }
                else if (errorData.message === 'That account has been disabled. Please contact "helpdesk@nearpeer.net" for additional help.') {
                    popupTitle = 'Signup Error';
                    popupMsg = MESSAGES.ACCOUNT_CLOSED;
                }
            }

            if (status === 403) {
                if (errorData.message === 'Sorry! Your Company and Email don’t match' || errorData.message === 'Sorry! Your College and Email don’t match') {
                    popupTitle = 'Signup Error';
                    popupMsg = errorData.message;
                }
            }

            if (errorData.message === 'Email has not yet been verified') {
                redux.dispatch(Push(AuthConstants.authFlow.routes.signup.verifyEmail));

                return null;
            }
            else if (errorData.message === 'Must reset password') {
                popupTitle = 'Reset Password';
                popupMsg = 'We previously received a password reset request for this account. For your protection, you’ll need to set a new password to log in. Please check your email for the password reset link to create your new password. If you haven’t received this email within a few minutes, please check your spam folder.';
            }
            else if (errorData.message === 'SSO account already exists for this email.') {
                const {
                    sso
                } = selectors.getSignupContext(redux.getState());

                internals.redirectToSSOLogin({
                    email,
                    schoolId,
                    sso
                });

                return null;
            }
            else if (errorData.message === 'Account already exists for this email.') {
                redux.dispatch(Push(AuthConstants.authFlow.routes.home));

                return MESSAGES.ALREADY_HAVE_ACCOUNT;
            }

            redux.dispatch(actions.alerts.notification.push({
                displayType: 'popUp',
                popUpTitle: popupTitle,
                message: popupMsg,
                action: { label: 'OK', onExecute: () => redux.dispatch(actions.alerts.notification.dismiss()) }
            }));

            return null;
        }],
        [Types.AUTH.LOGIN.FAILURE, ({ payload: { request, error } }) => {

            if (request?.token) {
                return null;
            }

            const errorData = error?.response?.data || {};
            const loginContext = selectors.getLoginContext?.(redux.getState()) || {};

            if (errorData.code === 'SSO_REQUIRED' && errorData.sso) {
                const authFlow = RoutingTable.resolveAuthFlow({
                    currentContext: loginContext,
                    email: errorData.email || request?.email,
                    preserveCurrentMfa: true,
                    response: errorData,
                    source: AuthConstants.authFlow.sources.passwordLoginSsoRequired
                });

                if (authFlow.method === 'sso') {
                    internals.redirectToSSOLogin({
                        email: authFlow.email,
                        schoolId: authFlow.schoolId,
                        sso: authFlow.sso
                    });

                    return null;
                }
            }

            if (errorData.message === 'Must reset password') {
                redux.dispatch(actions.alerts.notification.push({
                    displayType: 'popUp',
                    popUpTitle: 'Password Reset',
                    message: 'We previously received a password reset request for this account. For your protection, you’ll need to set a new password to log in. Please check your email for the password reset link to create your new password. If you haven’t received this email within a few minutes, please check your spam folder.',
                    action: { label: 'OK', onExecute: () => redux.dispatch(actions.alerts.notification.dismiss()) }
                }));

                return null;
            }

            redux.dispatch(actions.app.setAppMessage(internals.getLoginFailureMessage({
                errorData
            })));

            return null;
        }],
        [Types.AUTH.LOGIN_INPUT_FAILURE, MESSAGES.BAD_FIELDS],
        [Types.PROFILE_MANAGEMENT.CHANGE_USER_PASSWORD.FAILURE, ({ payload: { error } }) => {

            if (error?.response?.status === 400) {

                throw error;
            }
        }],
        [Types.PROFILE_MANAGEMENT.UPDATE_PROFILE.FAILURE, ({ payload: { error, request } }) => {

            return internals.getProfilePhoneCooldownMessage({ error, request });
        }],
        [Types.AUTH.RESET_PASSWORD.FAILURE, ({ payload: { error } }) => {

            if (error?.response?.status === 400) {

                throw error;
            }
        }],
        [Types.PROFILE_MANAGEMENT.UPDATE_PROFILE_PICTURE.FAILURE, ({ payload: { error } }) => {

            if (error?.response?.status === 413) {
                return MESSAGES.IMAGE_TOO_LARGE;
            }

            return MESSAGES.IMAGE_FAIL_DEFAULT;
        }],
        ...[
            [Types.DATA_FETCHING.FETCH_USER.FAILURE, AuthRoutes.app.welcome, 'This profile is no longer available.'],
            [Types.DATA_FETCHING.FETCH_OFFER.FAILURE, '/app/offers', 'This offer is no longer available.'],
            [Types.DATA_FETCHING.FETCH_RESOURCE.FAILURE, '/app/resources', 'This resource is no longer available.'],
            [Types.DATA_FETCHING.FETCH_SURVEY.FAILURE, AuthRoutes.app.welcome, 'This survey is no longer available.']
        ].map(([type, destination, message]) => [type, ({ payload: { error } }) => {

            if (error?.response?.status === 404) {
                redux.dispatch(Replace(destination));

                return message;
            }

            return MESSAGES.DEFAULT;
        }]),
        [Types.DATA_FETCHING.FETCH_SCHOOL_BY_TOKEN.FAILURE, ({ payload: { error, request } }) => {

            redux.dispatch(Push(AuthConstants.authFlow.routes.home));

            if (error.response.data.message === 'Account already exists for this email.') {
                return MESSAGES.ALREADY_HAVE_ACCOUNT;
            }
            else if (error.response.status === 404) {
                return MESSAGES.INVITE_LINK_EXPIRED;
            }
            else if (error.response.data.message === 'This link has expired. We sent a reminder email.') {
                return MESSAGES.LINK_EXPIRED_EMAIL_SENT;
            }
            else if (error.response.data.message === 'This link has expired.') {
                return MESSAGES.LINK_EXPIRED_SSO;
            }

            return MESSAGES.DEFAULT;
        }],
        [Types.SIGNUP.AUTHENTICATE.CHECK_PASSWORD.FAILURE, ({ payload: { error, request } }) => {

            redux.dispatch(Push(AuthConstants.authFlow.routes.home));

            if (error.response.data.message === 'Account already exists for this email.') {
                return MESSAGES.ALREADY_HAVE_ACCOUNT;
            }
            else if (error.response.status === 404) {
                return MESSAGES.INVITE_LINK_EXPIRED;
            }
            else if (error.response.data.message === 'This link has expired. We sent a reminder email.') {
                return MESSAGES.LINK_EXPIRED_EMAIL_SENT;
            }
            else if (error.response.data.message === 'This link has expired.') {
                return MESSAGES.LINK_EXPIRED_SSO;
            }

            return MESSAGES.DEFAULT;
        }],
        [Types.PROFILE_MANAGEMENT.CHANGE_USER_PASSWORD.FAILURE, ({ payload: { error } }) => {

            return error.response.data.message;
        }]
    ]
        .map((rule) => {

            if (typeof rule[1] !== 'function') {
                return [rule[0], () => rule[1]];
            }

            return rule;
        });

    const EXCEPT = {
        [Types.AUTH.LOGIN.FAILURE]: ({ payload: { error } }) => error && error.code === 'NO_TOKEN_ON_INIT',
        [Types.COMMUNICATION.MENTION.FAILURE]: () => true,
        [Types.DATA_FETCHING.FETCH_CURRENT_USER.FAILURE]: internals.isAuthFailure,
        [Types.DATA_FETCHING.FETCH_APP_ENTITIES.FAILURE]: internals.isAuthFailure,
        [Types.DATA_FETCHING.FETCH_NOTIFICATIONS.FAILURE]: () => true
    };

    const getMessageResult = (action) => {

        const response = action.payload?.error?.response;

        if (response?.status === 429 && internals.AUTH_RATE_LIMIT_FAILURES.has(action.type)) {
            internals.routeRateLimitedChallenge(action);

            return {
                handled: true,
                message: MfaCooldown.getRateLimitMessage(response.data)
            };
        }

        const { error, payload } = action;

        // First see if the server wants us to display this error
        if (!!error && payload && payload.response) {
            const { error: { response: { data } } } = payload;
            if (!!data.options && !!data.options.frontendDisplay) {
                return {
                    handled: true,
                    message: data.message
                };
            }
        }

        const rule = _.find(MESSAGE_SELECTION_RULES, (candidate) => candidate[0] === action.type);

        if (!rule) {
            return {
                handled: false,
                message: null
            };
        }

        const maybeFound = rule[1](action);

        if (maybeFound === MESSAGES.DEFAULT) {
            return {
                handled: false,
                message: null
            };
        }

        return {
            handled: true,
            message: maybeFound
        };
    };

    // Exceptions to the rule
    const isException = (action) => EXCEPT[action.type] && EXCEPT[action.type](action);

    return {
        messages: MESSAGES,
        pushNotification: ({ payload: { action } }) => {

            internals.recordReduxActionError(action);

            if (internals.shouldForceLogoutOnUnauthorized(action)) {
                return actions.auth.logoutSync();
            }

            if (isException(action)) {
                return;
            }

            const {
                handled,
                message: msg
            } = getMessageResult(action);

            if (msg) {
                return actions.alerts.notification.push({ message: msg, origin: action });
            }

            if (!handled && action.payload?.error) {
                // TODO send this up to the server to log the error
                safeThrow(action.payload.error);
            }

            if (selectors.getHasErrorNotificationsToShow(redux.getState())) {
                return actions.alerts.notification.show();
            }
        },
        showNext: ({ state }) => {

            if (selectors.getHasErrorNotificationsToShow(state)) {
                return actions.alerts.notification.show();
            }
        }
    };
};
